// Pairwise (reference x PLZ) matrix loading: fetch the metadata once, then fetch only the
// current reference's row with an HTTP Range request, falling back to the whole file if the
// server ignores Range (docs/design-isochrones.md §2.6, WP10 brief). Stateful (caches rows and,
// on fallback, the whole matrix) — kept separate from logic.ts, which must stay pure/DOM-free.
import { DATA_BASE, type PairwiseMeta, type RegistryMetric } from "./registry";

/** Thrown when the metric/meta_file/file triple is missing (a plain 404) — callers should treat
 * this as "the feature is not published yet", not a hard error. */
export class PairwiseUnavailableError extends Error {}

/**
 * Row-major uint8 matrix, `n x n`, keyed by `meta.plz_order` on both axes. Rows are fetched
 * lazily and cached per reference PLZ for the lifetime of this instance.
 */
export class PairwiseMatrix {
  private readonly plzToIndex = new Map<string, number>();
  /** Set once if a Range request ever comes back as a plain 200 (server ignored the header):
   * the whole file is then in memory and every subsequent row is sliced from it. */
  private fullMatrix: Uint8Array | null = null;
  private readonly rowCache = new Map<string, Uint8Array>();

  constructor(
    readonly meta: PairwiseMeta,
    private readonly fileUrl: string,
  ) {
    meta.plz_order.forEach((plz, i) => this.plzToIndex.set(plz, i));
  }

  indexOf(plz: string): number | undefined {
    return this.plzToIndex.get(plz);
  }

  /** Fetch (or return the cached copy of) the row for `refPlz`. `null` if `refPlz` is not in
   * `plz_order` (should not happen once the caller has validated meta against the PLZ index). */
  async rowFor(refPlz: string): Promise<Uint8Array | null> {
    const cached = this.rowCache.get(refPlz);
    if (cached) return cached;
    const idx = this.plzToIndex.get(refPlz);
    if (idx === undefined) return null;

    const n = this.meta.n;
    if (this.fullMatrix) {
      const row = this.fullMatrix.subarray(idx * n, (idx + 1) * n);
      this.rowCache.set(refPlz, row);
      return row;
    }

    const start = idx * n;
    const end = start + n - 1;
    const resp = await fetch(this.fileUrl, { headers: { Range: `bytes=${start}-${end}` } });
    if (!resp.ok) throw new Error(`fetch ${this.fileUrl}: HTTP ${resp.status}`);
    const buf = new Uint8Array(await resp.arrayBuffer());

    if (resp.status === 206) {
      this.rowCache.set(refPlz, buf);
      return buf;
    }
    // Status 200: the server ignored the Range header. Treat the body as the whole matrix,
    // cache it, and slice every row (including this one) from memory from now on.
    if (buf.length >= n * n) {
      this.fullMatrix = buf;
      const row = buf.subarray(start, start + n);
      this.rowCache.set(refPlz, row);
      return row;
    }
    // Neither a partial response nor a full matrix: best effort, cache what came back.
    this.rowCache.set(refPlz, buf);
    return buf;
  }
}

/**
 * Load a pairwise metric's metadata and return a matrix reader. Returns `null` (after logging
 * a console warning) when the metric isn't actually pairwise, or `meta_file`/`file` are unset —
 * a metric mis-declared in the registry, not a runtime data problem.
 * Throws `PairwiseUnavailableError` when `meta_file` 404s (data not published yet — the caller
 * should hide the dependent UI rather than show an error banner).
 */
export async function loadPairwiseMatrix(metric: RegistryMetric): Promise<PairwiseMatrix | null> {
  if (metric.kind !== "pairwise" || !metric.meta_file || !metric.file || !metric.encoding) {
    console.warn(`pairwise metric "${metric.id}" is missing meta_file/file/encoding in the registry`);
    return null;
  }
  const metaUrl = DATA_BASE + metric.meta_file;
  const resp = await fetch(metaUrl);
  // `vite dev`/`vite preview` answer a missing path with a 200 text/html SPA fallback rather
  // than a real 404 (see registry.ts's dataFileExists for the same quirk); a real static host
  // (GitHub Pages) gives a true 404. Treat both the same: the feature just isn't published yet.
  const contentType = resp.headers.get("content-type") ?? "";
  if (!resp.ok || contentType.includes("text/html")) {
    throw new PairwiseUnavailableError(`fetch ${metaUrl}: HTTP ${resp.status} (content-type "${contentType}")`);
  }
  const meta = (await resp.json()) as PairwiseMeta;
  return new PairwiseMatrix(meta, DATA_BASE + metric.file);
}
